import { SEASON_RANKINGS, TOP_COLLISIONS, TOP_LAPS } from '@/data/content';

export default function RankingsPage() {
  return (
    <div className="max-w-6xl mx-auto px-6 pt-8 pb-12">
      {/* ---- Header ---- */}
      <div className="mb-10">
        <h1 className="text-2xl font-extrabold text-text-primary mb-2">数据看板</h1>
        <p className="text-sm text-text-secondary">赛季排行 / 碰撞博物馆 / 圈速天梯 —— 每一场比赛都留下数据</p>
      </div>

      <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg p-5 mb-8 text-xs text-text-secondary space-y-2">
        <p><span className="text-text-disabled font-medium">ELO 积分制：</span>初始 1000 分，K=24。赢强手涨得多，输弱手扣得多——刷分无效，鼓励挑战更强的对手。仅方案 2/3 正式比赛录入积分。</p>
      </div>

      {/* ---- Season Selector ---- */}
      <div className="flex items-center gap-3 mb-8">
        <span className="text-xs text-text-disabled">赛季</span>
        {['2026 秋季', '2026 春季', '2025 秋季', '2025 春季'].map((s, i) => (
          <button key={s} className={`px-3 h-7 rounded-btn text-xs font-medium transition-colors ${i === 0 ? 'bg-arena-blue text-white' : 'bg-border-dark text-text-secondary hover:text-text-primary'}`}>{s}</button>
        ))}
      </div>

      {/* ---- Three Columns ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Column A: Rankings */}
        <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-arena-gold">赛季排行</h3>
            <span className="text-[10px] text-text-disabled">TOP 20</span>
          </div>
          <div className="space-y-0.5 max-h-[520px] overflow-y-auto scrollbar-hide">
            {SEASON_RANKINGS.map((e) => (
              <div key={e.rank} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-panel-mid/40 transition-colors">
                <span className={`w-5 text-right font-mono text-xs font-extrabold ${e.rank === 1 ? 'text-arena-gold' : e.rank <= 3 ? 'text-text-primary' : 'text-text-secondary'}`}>{e.rank}</span>
                <div className="w-6 h-6 rounded-full bg-border-dark shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text-primary truncate">{e.name}</p>
                  <p className="text-[10px] text-text-disabled">{e.team}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-mono text-text-primary">{e.score.toLocaleString()}</p>
                  <p className="text-[10px] text-arena-green">{e.winRate}% 胜率</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-border-dark text-center">
            <button className="text-xs text-arena-blue hover:underline">查看完整榜单</button>
          </div>
        </div>

        {/* Column B: Collision Museum */}
        <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-arena-red">碰撞博物馆</h3>
            <span className="text-[10px] text-text-disabled">本季最猛撞击</span>
          </div>
          <div className="space-y-2.5 mb-4">
            {TOP_COLLISIONS.map((c) => (
              <div key={c.rank} className="flex items-center gap-2 p-2 rounded hover:bg-panel-mid/40 transition-colors">
                <span className="text-[11px] font-mono text-text-disabled w-3">{c.rank}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs"><span className="text-arena-red font-medium">{c.redPlayer}</span><span className="text-text-disabled mx-1">vs</span><span className="text-arena-blue font-medium">{c.bluePlayer}</span></p>
                  <p className="text-[10px] text-text-disabled">{c.date}</p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-extrabold font-mono text-arena-red">{c.gForce}</span>
                  <span className="text-xs text-arena-red ml-0.5">G</span>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-panel-mid/40 rounded-card mecha-card mecha-bolt p-3 text-xs">
            <p className="text-text-disabled mb-2">碰撞力度说明</p>
            <div className="space-y-1">
              <div className="flex justify-between"><span className="text-text-secondary">1.0G 以下</span><span className="text-text-disabled">轻微接触，日常切磋级别</span></div>
              <div className="flex justify-between"><span className="text-arena-gold">1.5-2.5G</span><span className="text-text-disabled">中等碰撞，足以改变车辆姿态</span></div>
              <div className="flex justify-between"><span className="text-arena-red">3.0G+</span><span className="text-text-disabled">重型撞击，通常伴随 KO 或出界</span></div>
            </div>
          </div>
        </div>

        {/* Column C: Lap Records */}
        <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-arena-blue">圈速天梯</h3>
            <span className="text-[10px] text-text-disabled">各赛道最快</span>
          </div>
          <div className="space-y-2.5">
            {TOP_LAPS.map((lap, i) => (
              <div key={i} className="p-2.5 rounded hover:bg-panel-mid/40 transition-colors">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] text-text-secondary">{lap.track}</span>
                  <span className="text-lg font-extrabold font-mono text-arena-green">{lap.time}<span className="text-xs text-text-disabled font-sans ml-0.5">s</span></span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-primary">{lap.player}</span>
                  <span className="text-[10px] text-text-disabled truncate ml-2 max-w-[150px]">{lap.config}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-border-dark">
            <p className="text-[11px] text-text-disabled mb-1">赛道列表</p>
            <div className="flex flex-wrap gap-1">
              {['竞速赛道 A', '技术赛道 B', '综合赛道 C', '短道冲刺 D'].map((t) => (
                <span key={t} className="text-[10px] bg-border-dark text-text-secondary px-1.5 py-0.5 rounded-tag">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Season Summary Stats ---- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        {[
          { v: '156', l: '注册选手', c: '#00C2FF' },
          { v: '2,340', l: '本赛季比赛场次', c: '#00E676' },
          { v: '3.8', l: '赛季纪录最高 G 值', c: '#FF3B3B', u: 'G' },
          { v: '12.34', l: '竞速赛道 A 纪录', c: '#FFB800', u: 's' },
        ].map((s) => (
          <div key={s.l} className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-5 text-center">
            <p className="text-2xl font-extrabold font-mono" style={{ color: s.c }}>{s.v}<span className="text-sm ml-0.5">{s.u || ''}</span></p>
            <p className="text-xs text-text-disabled mt-2">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ---- Team Rankings ---- */}
      <h2 className="text-lg font-extrabold text-text-primary mb-4 text-center">团队赛战力榜</h2>
      <p className="text-xs text-text-secondary mb-4 text-center">2v2团队赛为独立赛道，不在单人赛季积分体系内。以下为团队赛历史战绩排名。</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {[
          { r:1, t:'雷霆双雄', m1:'铁犀牛', m2:'磐石', w:18, l:2 },
          { r:2, t:'疾风闪电', m1:'闪电侠', m2:'银月', w:15, l:4 },
          { r:3, t:'暗夜烈焰', m1:'暗夜猎手', m2:'烈焰风暴', w:13, l:5 },
        ].map((team) => (
          <div key={team.r} className="bg-panel-dark/40 border border-border-dark rounded-card mecha-card mecha-bolt p-5 hover:border-arena-blue/40 transition-all">
            <div className="flex items-center gap-3 mb-3">
              <span className={`text-xl font-extrabold font-mono ${team.r===1?'text-arena-gold':team.r===2?'text-text-primary':'text-text-secondary'}`}>#{team.r}</span>
              <div><h3 className="text-sm font-semibold text-text-primary">{team.t}</h3><p className="text-[11px] text-text-disabled">{team.m1} + {team.m2}</p></div>
            </div>
            <div className="flex gap-4 text-xs"><span className="text-arena-green">{team.w} 胜</span><span className="text-arena-red">{team.l} 负</span><span className="text-text-disabled">胜率 {Math.round(team.w/(team.w+team.l)*100)}%</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}
